/**
 * DEFLATE, decompressed by hand (RFC 1951).
 *
 * An `.xlsx` file is a ZIP archive, and everything inside a ZIP archive written by Excel is
 * deflated — so reading a spreadsheet at all begins here. There are three ways to get this
 * capability and only one of them is available on both sides of this codebase:
 *
 *  - a compression package: a third-party dependency that runs over an entire customer list,
 *    added for one file format, in a repository that writes its own PDF and its own SigV4;
 *  - the platform's `DecompressionStream`: present in browsers and in Node, but absent from
 *    the jsdom environment these tests run in, which would make the Excel path the one part
 *    of the import that no test can execute;
 *  - this file: about two hundred lines of a thirty-year-old specification that has not
 *    changed and cannot, working identically in a browser, in Node and in a test.
 *
 * The implementation is the canonical bit-by-bit decoder (the same shape as zlib's reference
 * `puff`), chosen over a table-driven one because a spreadsheet of a few thousand customers is
 * decoded once, by hand, while somebody watches a spinner — clarity is worth more here than
 * throughput, and being able to read this against the RFC is what makes it reviewable.
 */

/** Longest Huffman code DEFLATE permits. */
const MAX_CODE_BITS = 15;

/** A canonical Huffman table: how many codes of each length, and the symbols in order. */
interface HuffmanTable {
  /** `counts[n]` — how many codes are n bits long. Index 0 is unused. */
  readonly counts: Int32Array;
  /** Symbols sorted by code length, then by symbol. */
  readonly symbols: Int32Array;
}

/** Bit-level cursor. DEFLATE packs bits least-significant first within each byte. */
class BitReader {
  private byteIndex = 0;
  private bitBuffer = 0;
  private bitCount = 0;

  constructor(private readonly bytes: Uint8Array) {}

  readBit(): number {
    if (this.bitCount === 0) {
      if (this.byteIndex >= this.bytes.length) {
        throw new Error('Compressed data ended in the middle of a block');
      }
      this.bitBuffer = this.bytes[this.byteIndex] as number;
      this.byteIndex += 1;
      this.bitCount = 8;
    }
    const bit = this.bitBuffer & 1;
    this.bitBuffer >>= 1;
    this.bitCount -= 1;
    return bit;
  }

  readBits(count: number): number {
    let value = 0;
    for (let index = 0; index < count; index += 1) value |= this.readBit() << index;
    return value;
  }

  /** Drop the rest of the current byte — a stored block starts on a byte boundary. */
  alignToByte(): void {
    this.bitBuffer = 0;
    this.bitCount = 0;
  }

  /** Copy `length` raw bytes, for a stored (uncompressed) block. */
  readBytes(length: number): Uint8Array {
    if (this.byteIndex + length > this.bytes.length) {
      throw new Error('Compressed data ended in the middle of a stored block');
    }
    const slice = this.bytes.subarray(this.byteIndex, this.byteIndex + length);
    this.byteIndex += length;
    return slice;
  }
}

/** Build the canonical Huffman table described by a list of code lengths. */
function buildHuffmanTable(lengths: readonly number[]): HuffmanTable {
  const counts = new Int32Array(MAX_CODE_BITS + 1);
  for (const length of lengths) counts[length] = (counts[length] as number) + 1;
  // Length zero means "this symbol is not used", and must not be counted as a code.
  counts[0] = 0;

  const offsets = new Int32Array(MAX_CODE_BITS + 1);
  for (let bits = 1; bits <= MAX_CODE_BITS; bits += 1) {
    offsets[bits] = (offsets[bits - 1] as number) + (counts[bits - 1] as number);
  }

  const symbols = new Int32Array(lengths.length);
  for (let symbol = 0; symbol < lengths.length; symbol += 1) {
    const length = lengths[symbol] as number;
    if (length === 0) continue;
    symbols[offsets[length] as number] = symbol;
    offsets[length] = (offsets[length] as number) + 1;
  }

  return { counts, symbols };
}

/**
 * Read one symbol, one bit at a time.
 *
 * Canonical Huffman codes are ordered, so a decoder never needs the codes themselves: at each
 * length it is enough to know how many codes exist and where that length's symbols start.
 */
function decodeSymbol(reader: BitReader, table: HuffmanTable): number {
  let code = 0;
  let first = 0;
  let index = 0;
  for (let length = 1; length <= MAX_CODE_BITS; length += 1) {
    code |= reader.readBit();
    const count = table.counts[length] as number;
    if (code - first < count) return table.symbols[index + (code - first)] as number;
    index += count;
    first = (first + count) << 1;
    code <<= 1;
  }
  throw new Error('Invalid Huffman code in compressed data');
}

// The length and distance tables, straight out of RFC 1951 §3.2.5.
const LENGTH_BASE = [
  3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31, 35, 43, 51, 59, 67, 83, 99, 115, 131,
  163, 195, 227, 258,
] as const;
const LENGTH_EXTRA = [
  0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0,
] as const;
const DISTANCE_BASE = [
  1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193, 257, 385, 513, 769, 1025, 1537, 2049,
  3073, 4097, 6145, 8193, 12_289, 16_385, 24_577,
] as const;
const DISTANCE_EXTRA = [
  0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13,
] as const;
/** The order the code-length code lengths are written in (RFC 1951 §3.2.7). */
const CODE_LENGTH_ORDER = [
  16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15,
] as const;

let fixedLiterals: HuffmanTable | null = null;
let fixedDistances: HuffmanTable | null = null;

/** The fixed code tables, built once. Every stream that uses them uses the same ones. */
function fixedTables(): { literals: HuffmanTable; distances: HuffmanTable } {
  if (!fixedLiterals || !fixedDistances) {
    const literalLengths = new Array<number>(288);
    for (let symbol = 0; symbol < 288; symbol += 1) {
      literalLengths[symbol] = symbol < 144 ? 8 : symbol < 256 ? 9 : symbol < 280 ? 7 : 8;
    }
    fixedLiterals = buildHuffmanTable(literalLengths);
    fixedDistances = buildHuffmanTable(new Array<number>(30).fill(5));
  }
  return { literals: fixedLiterals, distances: fixedDistances };
}

/** Read the two dynamic tables a block carries in front of its data. */
function dynamicTables(reader: BitReader): { literals: HuffmanTable; distances: HuffmanTable } {
  const literalCount = reader.readBits(5) + 257;
  const distanceCount = reader.readBits(5) + 1;
  const codeLengthCount = reader.readBits(4) + 4;

  const codeLengthLengths = new Array<number>(19).fill(0);
  for (let index = 0; index < codeLengthCount; index += 1) {
    codeLengthLengths[CODE_LENGTH_ORDER[index] as number] = reader.readBits(3);
  }
  const codeLengthTable = buildHuffmanTable(codeLengthLengths);

  // Both tables are described by one run-length-encoded list, literals first.
  const lengths: number[] = [];
  while (lengths.length < literalCount + distanceCount) {
    const symbol = decodeSymbol(reader, codeLengthTable);
    if (symbol < 16) {
      lengths.push(symbol);
      continue;
    }
    let repeat: number;
    let value: number;
    if (symbol === 16) {
      const previous = lengths[lengths.length - 1];
      if (previous === undefined) throw new Error('Invalid code length repeat in compressed data');
      value = previous;
      repeat = 3 + reader.readBits(2);
    } else if (symbol === 17) {
      value = 0;
      repeat = 3 + reader.readBits(3);
    } else {
      value = 0;
      repeat = 11 + reader.readBits(7);
    }
    for (let index = 0; index < repeat; index += 1) lengths.push(value);
  }

  return {
    literals: buildHuffmanTable(lengths.slice(0, literalCount)),
    distances: buildHuffmanTable(lengths.slice(literalCount, literalCount + distanceCount)),
  };
}

/** Grow the output buffer geometrically — the decompressed size is not known up front. */
function ensureCapacity(output: Uint8Array, needed: number): Uint8Array {
  if (needed <= output.length) return output;
  let size = output.length === 0 ? 1024 : output.length;
  while (size < needed) size *= 2;
  const grown = new Uint8Array(size);
  grown.set(output);
  return grown;
}

/**
 * Decompress a raw DEFLATE stream — no zlib header, no gzip header, which is exactly what a
 * ZIP entry stores.
 */
export function inflateRaw(input: Uint8Array): Uint8Array {
  const reader = new BitReader(input);
  let output: Uint8Array = new Uint8Array(Math.max(1024, input.length * 4));
  let length = 0;

  const write = (byte: number): void => {
    output = ensureCapacity(output, length + 1);
    output[length] = byte;
    length += 1;
  };

  for (;;) {
    const final = reader.readBit();
    const type = reader.readBits(2);

    if (type === 0) {
      reader.alignToByte();
      const storedLength = reader.readBits(16);
      const complement = reader.readBits(16);
      if ((storedLength ^ 0xff_ff) !== complement) {
        throw new Error('Corrupt stored block in compressed data');
      }
      const bytes = reader.readBytes(storedLength);
      output = ensureCapacity(output, length + bytes.length);
      output.set(bytes, length);
      length += bytes.length;
    } else if (type === 1 || type === 2) {
      const { literals, distances } = type === 1 ? fixedTables() : dynamicTables(reader);
      for (;;) {
        const symbol = decodeSymbol(reader, literals);
        if (symbol < 256) {
          write(symbol);
          continue;
        }
        if (symbol === 256) break;

        const lengthIndex = symbol - 257;
        if (lengthIndex >= LENGTH_BASE.length) {
          throw new Error('Invalid length code in compressed data');
        }
        const copyLength =
          (LENGTH_BASE[lengthIndex] as number) +
          reader.readBits(LENGTH_EXTRA[lengthIndex] as number);

        const distanceIndex = decodeSymbol(reader, distances);
        if (distanceIndex >= DISTANCE_BASE.length) {
          throw new Error('Invalid distance code in compressed data');
        }
        const distance =
          (DISTANCE_BASE[distanceIndex] as number) +
          reader.readBits(DISTANCE_EXTRA[distanceIndex] as number);
        if (distance > length) throw new Error('Invalid back-reference in compressed data');

        // Byte by byte, and deliberately: the ranges overlap when a run is longer than the
        // distance, which is how DEFLATE encodes a repeat. A bulk copy would read the tail
        // it has not written yet.
        for (let index = 0; index < copyLength; index += 1) {
          write(output[length - distance] as number);
        }
      }
    } else {
      throw new Error('Invalid block type in compressed data');
    }

    if (final) break;
  }

  return output.subarray(0, length);
}
